/**
 * Dashboard page: welcome, client overview, financial overview, goals, saved strategies & case studies, next steps, workflow card.
 */
import { mergeSessionPlans } from './sessionPlans.ts';
import React, { useMemo, useEffect, useState } from 'react';
import { Building, FileText, Home, Users, DollarSign, Info, ChevronDown, ChevronUp, Lightbulb, Wallet, TrendingUp, Landmark } from 'lucide-react';
import * as calculationApi from '../../services/calculationApi.ts';
import { isIllnessSeparatedCouple } from '../../../../shared/src/calculation/householdStatus.ts';
import { formatCurrency, calculateAge, getInitials } from '../../utils/index.ts';
import { Card, FormSelect, IconTile } from '../../components/ui/index.tsx';
import { MyAgedCareOfficialLogo } from '../../components/branding/index.tsx';
import { SectionHeader, ProfileCard, InfoPill, GoalItem, NextStepsCard, HeroMetric } from './DashboardHelpers.tsx';
import { StartHereCard, ProfileSummaryStrip, FoldedPlansNote, goalPossessive, type StartHereAction } from './StartHere.tsx';
import { REPORTS_SECTION_ID } from '../../components/layout/clientMenu.ts';
import WorkflowApplicationsFormsCard from './WorkflowCard.tsx';
import { openAgedCareDigitalSOAAttachment } from '../../features/agedCare/AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { openGfSolicitorBrief } from '../../features/grannyFlat/gfSolicitorBrief.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy, SavedCaseStudy } from './dashboardConfig.tsx';
import { evaluateProfileCompleteness } from '../../../../shared/src/factFind/profileCompleteness.ts';

/** "michael's" -> "Michael's", "your and Tara's" -> "Your and Tara's". */
const capitaliseFirst = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/** Plain words for the residency codes, for the first-visit summary strip. */
const RESIDENCY_LABELS: Record<string, string> = {
    own_home: 'Own home',
    renting: 'Renting',
    retirement_village: 'Retirement village',
    granny_flat: 'Granny flat',
    with_family: 'With family',
    aged_care: 'Residential aged care',
};

export interface DashboardProps {
    setActivePage: (page: string) => void;
    /**
     * Jumps the fact-find to a given step. Optional so the Dashboard still renders without it (the
     * professional portal mounts it in places that have no profile stepper); the "Edit details" link
     * falls back to opening the profile at whatever step it was left on.
     */
    setClientProfileStep?: (step: number) => void;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    savedStrategies: SavedStrategy[];
    setSavedStrategies: React.Dispatch<React.SetStateAction<SavedStrategy[]>>;
    savedCaseStudies: SavedCaseStudy[];
    setSavedCaseStudies: React.Dispatch<React.SetStateAction<SavedCaseStudy[]>>;
    onDeleteDraftSoa?: ((id: string | number) => void) | null;
    professionalContext?: {
        enabled?: boolean;
        caseId?: string;
        accessMode?: string;
        submissionDate?: string | number | Date;
    } | null;
    onSubmitToAdviser?: (() => void) | null;
}

interface DashboardFinancials {
    netWorth: number;
    cashFlow: number;
    socialSecurity: number;
}

/**
 * Who produced a saved plan — the engine's proposal, or an adviser working the levers themselves.
 *
 * Asked for by the owner (11 Sep 2026): coming back to a list of plans weeks later, it matters which
 * ones you reasoned your way to and which were suggested to you.
 *
 * Words rather than the "SE"/"MC" initials used on SOA cards, because CLIENTS read this dashboard and
 * a two-letter code means nothing to them. Plans saved before the source was recorded carry none, and
 * get no badge — an unlabelled plan is honest, a wrongly-labelled one is not.
 */
function PlanSourceBadge({ source }: { source?: unknown }) {
    const label =
        source === 'suggestion_engine' ? 'Suggested' : source === 'manual_control' ? 'Manual' : null;
    if (!label) return null;
    return (
        <span
            className="ml-2 inline-block align-middle rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
            title={
                label === 'Suggested'
                    ? 'Built by the suggestion engine'
                    : 'Built by hand in manual control'
            }
        >
            {label}
        </span>
    );
}

export default function Dashboard({ setActivePage, setClientProfileStep, factFind, setFactFind, savedStrategies, setSavedStrategies, savedCaseStudies, setSavedCaseStudies, onDeleteDraftSoa = null, professionalContext = null, onSubmitToAdviser = null }: DashboardProps) {
    const { client, partner, adviceFor, family, goals } = factFind;
    // The same rule the sidebar's "Complete" tag uses, so the two never disagree about the profile.
    const profileCompleteness = useMemo(() => evaluateProfileCompleteness(factFind as unknown as FactFind), [factFind]);
    const isPartnered = adviceFor === 'couple';
    const [showCoupleMeansTestPanel, setShowCoupleMeansTestPanel] = useState(false);

    const [{ netWorth, cashFlow, socialSecurity }, setFinancials] = useState<DashboardFinancials>({ netWorth: 0, cashFlow: 0, socialSecurity: 0 });
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((result) => {
            if (!cancelled) setFinancials(result as unknown as DashboardFinancials);
        });
        return () => { cancelled = true; };
    }, [factFind]);

    const dashboardCouplePensionLabel = useMemo(() => {
        if (factFind?.adviceFor !== 'couple') return '';
        return isIllnessSeparatedCouple(factFind as unknown as FactFind, {}) ? 'illness-separated couple' : 'standard couple';
    }, [factFind]);

    // Adds the session copy's plans; never replaces the list (sessionPlans.ts says why).
    useEffect(() => {
        const stored = sessionStorage.getItem('careCompanionStrategies');
        if (stored) {
            try {
                const fromSession: unknown = JSON.parse(stored);
                setSavedStrategies((prev) => mergeSessionPlans(prev || [], fromSession));
            } catch (e) {
                console.error('Error parsing saved strategies:', e);
            }
        }
    }, [factFind, setSavedStrategies]);

    useEffect(() => {
        const stored = sessionStorage.getItem('careCompanionScenarios');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setSavedCaseStudies(Array.isArray(parsed) ? parsed : []);
            } catch (e) {
                console.error('Error parsing saved case studies:', e);
            }
        }
    }, [factFind, setSavedCaseStudies]);

    const reopenStrategy = (strategy: SavedStrategy) => {
        sessionStorage.setItem('careCompanionReopenStrategy', JSON.stringify(strategy));
        setActivePage('AgedCarePage');
    };

    const reopenHomeCareStrategy = (strategy: SavedStrategy) => {
        sessionStorage.setItem('careCompanionReopenHomeCareStrategy', JSON.stringify(strategy));
        setActivePage('HomeCarePage');
    };

    const reopenSoaScenario = (scenario: SavedCaseStudy) => {
        if (scenario?.soaSnapshot) {
            // SavedCaseStudy.soaSnapshot (dashboardConfig.tsx) is deliberately loose — it's whatever was
            // persisted to storage; openAgedCareDigitalSOAAttachment's SoaSnapshot (agedCare, out of
            // scope here) is the strict in-memory shape produced right before persisting the same data.
            openAgedCareDigitalSOAAttachment(scenario.soaSnapshot as unknown as Parameters<typeof openAgedCareDigitalSOAAttachment>[0]);
            return;
        }
        setActivePage('AgedCarePage');
    };

    const visibleCaseStudies = useMemo(() => {
        if (!professionalContext?.enabled || !professionalContext?.caseId) return savedCaseStudies;
        return savedCaseStudies.filter((scenario) => !scenario.caseId || scenario.caseId === professionalContext.caseId);
    }, [savedCaseStudies, professionalContext]);

    const clientData = {
        name: [client?.firstName || 'Applicant', client?.lastName].filter(Boolean).join(' '),
        health: client?.healthStatus ? client.healthStatus.charAt(0).toUpperCase() + client.healthStatus.slice(1) : 'N/A',
        age: calculateAge(client?.dob),
        initials: getInitials(client?.firstName, client?.lastName),
        color1: '#89CFF0',
        color2: '#4682B4'
    };

    const partnerData = {
        name: `${partner?.firstName || 'Partner'} ${partner?.lastName || ''}`,
        health: partner?.healthStatus ? partner.healthStatus.charAt(0).toUpperCase() + partner.healthStatus.slice(1) : 'N/A',
        age: calculateAge(partner?.dob),
        initials: getInitials(partner?.firstName, partner?.lastName),
        color1: '#F4C2C2',
        color2: '#E08D8D'
    };

    const livingArrangementGoals = [
        goals?.home_care && "Access Home Care Support",
        goals?.downsize_home && "Downsize the family home",
        goals?.retirement_village && "Move to a retirement village",
        goals?.granny_flat && "Explore granny flat options",
        goals?.aged_care && "Move into residential aged care",
    ].filter(Boolean) as string[];

    const financialGoals = [
        goals?.meet_costs && "Meet Care Costs",
        goals?.maximise_support && "Maximise Government Support",
        goals?.preserve_assets && "Preserve Assets For Inheritance",
        goals?.funding_guidance && "Guidance On Funding Options",
    ].filter(Boolean) as string[];

    const userName = factFind.completer !== 'client' ? (factFind.completerFirstName || 'User') : (client?.firstName || 'User');

    /*
     * FIRST VISIT: nothing modelled yet. See StartHere.tsx for why this state exists and why the
     * test is "nothing saved" rather than "has been here before".
     *
     * `showAllSections` lets the reader open the full dashboard anyway — nothing is hidden from
     * anyone who goes looking, and the moment a plan is saved this whole branch stops applying.
     */
    /*
     * Your Details and Goals Summary are permanently superseded by the ProfileSummaryStrip above:
     * they say the same things in a longer form, and meeting the same facts twice in two different
     * layouts reads as a fault rather than as a summary (reported 2026-08-22). Kept behind a named
     * flag rather than deleted, because the per-person cards carry health status and the children
     * pill, and if the strip ever proves too terse this is where the richer version lives.
     */
    const showLegacyProfileSections = false;
    const [showAllSections, setShowAllSections] = useState(false);
    const hasNothingSaved = savedStrategies.length === 0 && visibleCaseStudies.length === 0;
    const isFirstVisit = hasNothingSaved && !showAllSections;

    /**
     * Name-led, matching the fact-find: a son filling this in for his father reads "George's".
     *
     * The HEADING names the whole household, because the profile covers the whole household. Each
     * ACTION names whoever that goal is for, which the profile records separately for residential
     * care and home care — one of a couple can be entering care while both are on home care.
     */
    const readerIsApplicant = factFind.completer === 'client' || !client?.firstName;
    const householdNames = { client: client?.firstName, partner: partner?.firstName };
    const ApplicantPossessive = capitaliseFirst(
        goalPossessive(undefined, householdNames, isPartnered, readerIsApplicant),
    );
    const possessiveForAction = (action: StartHereAction) =>
        goalPossessive(
            action.forField ? (goals as Record<string, string | undefined>)?.[action.forField] : undefined,
            householdNames,
            isPartnered,
            readerIsApplicant,
        );

    const openProfile = () => {
        // Step 2 is Your Details — the step someone means by "edit details". Without the setter we
        // still open the profile, just wherever it was left.
        setClientProfileStep?.(2);
        setActivePage('Client Profile');
    };

    /**
     * The first-visit summary. Each person carries their OWN age — the first cut appended one age to
     * a list of two names, so a couple read as "Michael Sooky, 83, and Tara Sooky" and Tara's age
     * silently belonged to Michael (reported 2026-08-22).
     */
    const describePerson = (person: { name: string; age: number | null }): string =>
        person.age ? `${person.name} (${person.age})` : person.name;

    const summaryFacts = [
        {
            label: isPartnered ? 'Who' : 'Who',
            value: isPartnered
                ? `${describePerson(clientData)} and ${describePerson(partnerData)}`
                : `${describePerson(clientData)}, single`,
        },
        { label: 'Lives', value: RESIDENCY_LABELS[String(client?.currentResidency || '')] || 'Not recorded' },
        // Living and financial goals are listed apart, as they are everywhere else in the tool: one
        // run-on line of six goals is a wall of text (reported 2026-08-22).
        ...(livingArrangementGoals.length
            ? [{ label: livingArrangementGoals.length === 1 ? 'Living goal' : 'Living goals', value: livingArrangementGoals.join(' · ') }]
            : []),
        ...(financialGoals.length
            ? [{ label: financialGoals.length === 1 ? 'Financial goal' : 'Financial goals', value: financialGoals.join(' · ') }]
            : []),
        ...(livingArrangementGoals.length + financialGoals.length === 0
            ? [{ label: 'Goals', value: 'None set' }]
            : []),
    ];

    return (
        <div className="space-y-8 relative min-h-screen">
            <div className="relative overflow-hidden rounded-3xl shadow-cardlg bg-gradient-to-br from-brand-navy via-blue-800 to-brand-blue">
                <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
                <div className="absolute -bottom-24 -left-12 w-80 h-80 rounded-full bg-teal-300/10 blur-3xl" aria-hidden="true" />
                <div className="relative p-7 md:p-9">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 mb-7">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-white text-xl font-bold">
                                {getInitials(client?.firstName, client?.lastName) || 'U'}
                            </div>
                            <div>
                                <p className="text-blue-100/80 text-sm font-medium">Welcome back</p>
                                <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">{userName}</h1>
                            </div>
                        </div>
                        {/* The Start here card below says what to do, far more plainly than this ever
                            did — it was the smallest text on the page, pale on navy, in the corner.
                            That card is now permanent, so this is redundant for EVERY reader rather
                            than just a new one, and two instructions competing in one hero is worse
                            than the clearer one alone. */}
                        {false && (
                            <p className="text-sm text-blue-100/80 max-w-xs sm:text-right leading-relaxed">Here&apos;s your financial snapshot, based on the details you&apos;ve entered. Explore your options and reports below — your information is saved, so you can revisit any section any time.</p>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <HeroMetric label="Net Worth" value={netWorth} icon={Wallet} />
                        <HeroMetric label="Annual Cash Flow" value={cashFlow} icon={TrendingUp} signed />
                        <HeroMetric label="Age Pension" value={socialSecurity} icon={Landmark} />
                    </div>
                    {!isPartnered && (
                        <p className="mt-4 text-xs text-blue-100/80 leading-relaxed max-w-2xl">
                            {socialSecurity > 0
                                ? "Your Age Pension figure is an estimate under Centrelink's single-person income and assets tests."
                                : "Based on your savings and assets, you're currently above the Age Pension limit for a single person — so no pension is payable right now. This can change if your assessable assets reduce (for example, after paying for care)."}
                        </p>
                    )}
                </div>
            </div>

            {/* FIRST VISIT — see StartHere.tsx. Lead with the next action, fold the empty shelves,
                and keep a route back into the fact-find. Everything is one click away. */}
            {/*
             * ONE PAGE IN TWO STATES, not two pages.
             *
             * The first cut swapped the whole layout when "Show all sections" was clicked, and the
             * reader "immediately thought something was wrong" — nothing they had been looking at
             * survived the click (reported 2026-08-22). So the Start here card stays exactly where it
             * is while there is nothing saved, and expanding only ADDS the rest of the dashboard
             * beneath it. Nothing above the fold moves.
             *
             * The compact profile strip is dropped once expanded, because the full page carries Your
             * Details and Goals Summary properly and two versions of the same facts is worse than
             * either.
             */}
            {/*
             * ALWAYS ON, not first-visit-only (owner-caught, 2026-08-26: "the dashboard reverts to
             * the older style once you save a strategy").
             *
             * What made this layout better was never that the reader was new — it was the hierarchy:
             * lead with the next action, state the profile compactly, fold what is empty. Those stay
             * true the moment a plan is saved. Swapping the whole page at that point punished the
             * reader for making progress, and meant the better design was only ever seen by people
             * who had not used the product yet.
             *
             * So the STRUCTURE is now constant and only the CONTENT changes: the folded plans note
             * fills in with real plans. Your Details and Goals Summary stay suppressed below, because
             * the compact strip says the same things and meeting the same facts twice in two layouts
             * reads as a fault (reported 2026-08-22).
             */}
            {(
                <div className="space-y-6">
                    <StartHereCard goals={goals as Record<string, unknown>} completeness={profileCompleteness} setClientProfileStep={setClientProfileStep} Possessive={ApplicantPossessive} possessiveFor={possessiveForAction} setActivePage={setActivePage} />
                    {/* The compact strip stays when the page expands. It says the same things as Your
                        Details and Goals Summary, so those two are suppressed below instead — keeping
                        all three meant the reader met the same facts in two different layouts one
                        after the other, which read as a fault rather than as a summary
                        (reported 2026-08-22). */}
                    <ProfileSummaryStrip facts={summaryFacts} onEdit={openProfile} />
                </div>
            )}

            {isFirstVisit ? (
                <div className="space-y-6">
                    <FoldedPlansNote onShowAll={() => setShowAllSections(true)} />
                    <Card className="bg-white/70">
                        <SectionHeader title="Helpful Services" showArrow={false} />
                        <div className="mt-6 p-4 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                            <MyAgedCareOfficialLogo />
                        </div>
                    </Card>
                </div>
            ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                <div className="xl:col-span-2 space-y-8">
                    {showLegacyProfileSections && (
                    <Card className="bg-white/70">
                        <SectionHeader title="Your Details" />
                        <div className="mt-6">
                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                <ProfileCard user={clientData} />
                                {isPartnered && <ProfileCard user={partnerData} />}
                            </div>
                            {((parseFloat(String(family?.childrenCount)) || 0) > 0 || client?.currentResidency === 'own_home') && (
                                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                                    {(parseFloat(String(family?.childrenCount)) || 0) > 0 && <InfoPill icon={Users} text={`${family?.childrenCount} Children`} />}
                                    {client?.currentResidency === 'own_home' && <InfoPill icon={Home} text="Home Owner" />}
                                </div>
                            )}
                        </div>
                    </Card>
                    )}

                    {showLegacyProfileSections && (
                    <Card className="bg-white/70">
                        <SectionHeader title="Goals Summary" />
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                                <div className="flex items-center gap-3 mb-4">
                                    <IconTile icon={Home} tone="brand" variant="solid" size="sm" />
                                    <p className="font-bold text-gray-800">Living Arrangement Goals</p>
                                </div>
                                {livingArrangementGoals.length > 0 ? <div>{livingArrangementGoals.map(g => <GoalItem key={g} text={g} />)}</div> : <p className="text-gray-500 text-sm italic">No goals set.</p>}
                            </div>
                            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                                <div className="flex items-center gap-3 mb-4">
                                    <IconTile icon={DollarSign} tone="brand" variant="solid" size="sm" />
                                    <p className="font-bold text-gray-800">Financial Goals</p>
                                </div>
                                {financialGoals.length > 0 ? <div>{financialGoals.map(g => <GoalItem key={g} text={g} />)}</div> : <p className="text-gray-500 text-sm italic">No goals set.</p>}
                            </div>
                        </div>
                    </Card>
                    )}

                    {isPartnered && (
                        <Card className="bg-white border border-slate-200/70 border-l-4 border-blue-500">
                            <button
                                type="button"
                                onClick={() => setShowCoupleMeansTestPanel(!showCoupleMeansTestPanel)}
                                className="w-full p-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors duration-200 rounded-lg"
                            >
                                <div className="flex items-center gap-3">
                                    <Info className="text-blue-600 flex-shrink-0" size={20} />
                                    <div>
                                        <h3 className="text-blue-900 font-semibold text-sm">Couple means test (Age Pension snapshot)</h3>
                                        <p className="text-xs text-blue-700/90 mt-0.5">How your Age Pension figure above is assessed</p>
                                    </div>
                                </div>
                                <ChevronDown
                                    className={`text-blue-600 transition-transform duration-200 flex-shrink-0 ${showCoupleMeansTestPanel ? 'rotate-180' : ''}`}
                                    size={18}
                                />
                            </button>
                            {showCoupleMeansTestPanel && (
                                <div className="px-4 pb-4 pt-0 space-y-4 text-sm text-blue-900 border-t border-blue-100/80">
                                    <p className="leading-relaxed">
                                        The <strong>Age Pension</strong> amount above uses Centrelink&apos;s{' '}
                                        <strong>{dashboardCouplePensionLabel}</strong> income and assets test thresholds.
                                    </p>
                                    <p className="leading-relaxed">
                                        In the <strong>Aged Care</strong> module we apply Centrelink-style rules whereby, when one person moves into aged care, a different <strong>&ldquo;illness-separated&rdquo;</strong> assessment threshold is used. This provides higher thresholds to reflect the higher costs associated with care. The drop-down below lets you override this back to the normal (more conservative) standard-couple threshold.
                                    </p>
                                    <FormSelect
                                        label="Illness-separated couple treatment"
                                        name="illnessSeparatedMode"
                                        value={factFind.relationship?.illnessSeparatedMode || 'auto'}
                                        onChange={(name, value) =>
                                            setFactFind((prev) => ({
                                                ...prev,
                                                relationship: { ...prev.relationship, [name]: value },
                                            }))
                                        }
                                    >
                                        <option value="auto">Auto (recommended)</option>
                                        <option value="yes">Yes — apply illness-separated rules</option>
                                        <option value="no">No — standard couple (not illness-separated)</option>
                                    </FormSelect>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* DEFERRED (needs backend): client completion + "notify my adviser" handoff. This handover card is the
                        existing scaffolding but only shows in the professional-portal "client_stop_dashboard" mode, which
                        the standard app does not pass — so a client who finishes the fact-find has no way to signal "I'm
                        done" or notify their adviser, and entering data is wrongly treated as "finished". When the backend
                        lands, surface a completion/notify affordance in the STANDARD client flow (gate on factFind.completer
                        === 'client') and wire onSubmitToAdviser to a real notification. See memory:
                        deferred-client-completion-handoff. */}
                    {professionalContext?.enabled && professionalContext.accessMode === 'client_stop_dashboard' && (
                        <Card className="bg-white border border-slate-200/70 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Professional Handover</h3>
                                    <p className="text-sm text-gray-700 mt-1">
                                        This case is configured to stop at Dashboard. Submit profile data to your adviser for modelling and strategy review.
                                    </p>
                                    <p className="text-xs text-gray-600 mt-2">
                                        Submission date: {professionalContext.submissionDate ? new Date(professionalContext.submissionDate).toLocaleString('en-AU') : 'Not submitted yet'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => onSubmitToAdviser && onSubmitToAdviser()}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                                >
                                    Submit to Adviser
                                </button>
                            </div>
                        </Card>
                    )}

                    <Card className="bg-white/70">
                        <SectionHeader title="Your Saved Plans" />
                        <div className="mt-6 space-y-5">
                            {goals?.aged_care && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Building} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Aged Care</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'aged_care').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('AgedCarePage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    {savedStrategies.filter(s => s.type === 'aged_care').length > 0 ? (
                                        <div className="space-y-3">
                                            {savedStrategies.filter(s => s.type === 'aged_care').map(strategy => (
                                                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => reopenStrategy(strategy)}>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                        <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right text-xs space-y-1 ml-4">
                                                        <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(strategy.outputs?.legacyEstimate)}</p>
                                                        <p className="text-gray-600"><span className="font-medium">Cost:</span> {formatCurrency(strategy.outputs?.agedCareCost)}</p>
                                                        <p className="text-gray-600"><span className="font-medium">Room:</span> {formatCurrency(strategy.outputs?.roomCost || 0)}</p>
                                                        <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Click to reopen →</p>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="mt-4 p-4 bg-slate-50 border-l-4 border-blue-500 rounded-r-lg flex items-start gap-2.5">
                                                <Lightbulb size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                                <p className="text-sm text-blue-800"><strong>Want to model a different facility?</strong> Click "Aged Care" to explore alternative room costs and create new strategies for comparison.</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-gray-300">
                                                <Building className="mx-auto text-gray-300 mb-3" size={32} />
                                                <p className="text-gray-500 text-sm font-medium mb-2">No aged care strategies saved yet.</p>
                                            </div>
                                            <div className="p-4 bg-slate-50 border-l-4 border-blue-500 rounded-r-lg flex items-start gap-2.5">
                                                <Lightbulb size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                                <p className="text-sm text-blue-800"><strong>Ready to start?</strong> Click "Aged Care" to begin modeling your aged care funding options and create your first strategy.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {visibleCaseStudies.length > 0 && (
                                /* The menu's Reports line lands here (components/layout/clientMenu.ts). */
                                <div id={REPORTS_SECTION_ID} className="scroll-mt-4 border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={FileText} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">SOA Generated Scenarios</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{visibleCaseStudies.length} saved</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        {visibleCaseStudies.map(scenario => (
                                            <div key={scenario.id} onClick={() => reopenSoaScenario(scenario)} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group">
                                                <div className="flex-1">
                                                    {(() => {
                                                        const sourceTag = scenario.soaSource || (scenario.soaSnapshot?.strategySource === 'manual_control' ? 'MC' : scenario.soaSnapshot?.strategySource === 'suggestion_engine' ? 'SE' : '');
                                                        return (
                                                    <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {scenario.name}
                                                        {sourceTag && (
                                                            <span className="ml-2 inline-block text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 align-middle">
                                                                {sourceTag}
                                                            </span>
                                                        )}
                                                    </p>
                                                        );
                                                    })()}
                                                    <p className="text-xs text-gray-500 mt-1">{new Date(scenario.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                </div>
                                                <div className="text-right text-xs space-y-1 ml-4">
                                                    <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(scenario.outputs?.legacyEstimate)}</p>
                                                    <p className="text-gray-600"><span className="font-medium">Cost:</span> {formatCurrency(scenario.outputs?.agedCareCost)}</p>
                                                    <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Click to reopen SOA →</p>
                                                    {(scenario.soaDeliveryStatus || 'draft') === 'draft' && (
                                                        <button
                                                            type="button"
                                                            className="mt-2 text-red-600 hover:text-red-700 font-semibold"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDeleteDraftSoa?.(scenario.id);
                                                            }}
                                                        >
                                                            Delete Draft
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {goals?.home_care && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Home} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Home Care</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'home_care').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('HomeCarePage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    {savedStrategies.filter(s => s.type === 'home_care').length > 0 ? (
                                        <div className="space-y-3">
                                            {savedStrategies.filter(s => s.type === 'home_care').map(strategy => (
                                                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => reopenHomeCareStrategy(strategy)}>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                        <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right text-xs space-y-1 ml-4">
                                                        <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(strategy.outputs?.legacyEstimate || 0)}</p>
                                                        <p className="text-gray-600"><span className="font-medium">Cost:</span> {formatCurrency(strategy.outputs?.annualContribution || strategy.outputs?.totalCost || 0)}</p>
                                                        <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Click to reopen →</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-gray-300">
                                            <Home className="mx-auto text-gray-300 mb-3" size={32} />
                                            <p className="text-gray-500 text-sm font-medium">No home care strategies saved yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {goals?.retirement_village && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Users} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Retirement Village</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'retirement_village').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('RetirementVillagePage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    {/* Render the actual saved strategies — this block used to hardcode the empty
                                        state while the badge above counted real saves ("1 saved" next to "none yet"). */}
                                    {savedStrategies.filter(s => s.type === 'retirement_village').length > 0 ? (
                                        <div className="space-y-3">
                                            {savedStrategies.filter(s => s.type === 'retirement_village').map(strategy => (
                                                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => setActivePage('RetirementVillagePage')}>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                        <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right text-xs space-y-1 ml-4">
                                                        <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(strategy.outputs?.legacyEstimate || 0)}</p>
                                                        <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Open Retirement Village →</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-gray-300">
                                            <Users className="mx-auto text-gray-300 mb-3" size={32} />
                                            <p className="text-gray-500 text-sm font-medium">No retirement village strategies saved yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {goals?.downsize_home && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Home} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Downsizing</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'downsizer').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('DownsizingPage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    {savedStrategies.filter(s => s.type === 'downsizer').length > 0 ? (
                                        <div className="space-y-3">
                                            {savedStrategies.filter(s => s.type === 'downsizer').map(strategy => (
                                                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => setActivePage('DownsizingPage')}>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                        <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right text-xs space-y-1 ml-4">
                                                        <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(strategy.outputs?.legacyEstimate || 0)}</p>
                                                        <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Open Downsizing →</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-gray-300">
                                            <Home className="mx-auto text-gray-300 mb-3" size={32} />
                                            <p className="text-gray-500 text-sm font-medium">No downsizing strategies saved yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/*
                              * CASHFLOW & AGE PENSION — gated on a SAVED PLAN, not on a goal, and
                              * that break from the pattern is deliberate (owner, 2026-08-28).
                              *
                              * It is not a goal a client ticks; it is a tool reached from inside
                              * Support at Home. And the owner wants this screen kept simple and
                              * transitional, so a permanently-visible empty section would be exactly
                              * the noise they are guarding against. A client who never opens the
                              * module never sees this at all.
                              */}
                            {savedStrategies.filter(s => s.type === 'cashflow_pension').length > 0 && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Landmark} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Cashflow &amp; Age Pension</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'cashflow_pension').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('CashflowPensionPage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    <div className="space-y-3">
                                        {savedStrategies.filter(s => s.type === 'cashflow_pension').map(strategy => (
                                            <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => setActivePage('CashflowPensionPage')}>
                                                <div className="flex-1">
                                                    <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                </div>
                                                <div className="text-right text-xs space-y-1 ml-4">
                                                    {/*
                                                      * All three, never just the flattering one. Most of
                                                      * these levers buy a bigger pension by spending
                                                      * capital, so a card showing the pension alone would
                                                      * be selling rather than advising.
                                                      */}
                                                    <p className="text-gray-600"><span className="font-medium">Age Pension:</span> {formatCurrency(strategy.outputs?.agePension || 0)}</p>
                                                    <p className="text-gray-600"><span className="font-medium">Cashflow:</span> {formatCurrency(strategy.outputs?.cashFlow || 0)}</p>
                                                    <p className="text-gray-600"><span className="font-medium">Estate:</span> {formatCurrency(strategy.outputs?.legacyEstimate || 0)}</p>
                                                    <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Open Cashflow &amp; Age Pension →</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {goals?.granny_flat && (
                                <div className="border-l-4 border-blue-500 rounded-lg bg-white border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <IconTile icon={Home} tone="brand" variant="solid" size="md" />
                                            <div>
                                                <h3 className="font-bold text-gray-800">Granny Flat</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-semibold">{savedStrategies.filter(s => s.type === 'granny_flat').length} saved</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setActivePage('GrannyFlatPage')} className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:underline transition-all">View All →</button>
                                    </div>
                                    {savedStrategies.filter(s => s.type === 'granny_flat').length > 0 ? (
                                        <div className="space-y-3">
                                            {savedStrategies.filter(s => s.type === 'granny_flat').map(strategy => (
                                                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all duration-300 group" onClick={() => setActivePage('GrannyFlatPage')}>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {strategy.name}
                                                        <PlanSourceBadge source={strategy.source} />
                                                    </p>
                                                        <p className="text-xs text-gray-500 mt-1">{new Date(strategy.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right text-xs space-y-1 ml-4">
                                                        <p className="text-gray-600"><span className="font-medium">Legacy:</span> {formatCurrency(strategy.outputs?.legacyEstimate || 0)}</p>
                                                        <p className="text-blue-600 font-semibold mt-2 group-hover:underline">Open Granny Flat →</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                    <div className="p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-gray-300">
                                        <Home className="mx-auto text-gray-300 mb-3" size={32} />
                                        <p className="text-gray-500 text-sm font-medium">No granny flat strategies saved yet.</p>
                                    </div>
                                    )}
                                    {/* The family discussion guide's solicitor brief, kept with the file (owner, 3 Oct 2026). */}
                                    {factFind?.grannyFlat?.familyGuide?.brief?.generatedAt && (
                                        <div className="mt-3 flex items-center justify-between gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <Users size={18} className="mt-0.5 shrink-0 text-blue-600" />
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-gray-900">Family discussion guide — solicitor brief</p>
                                                    <p className="text-xs text-gray-500 mt-1">Produced {new Date(factFind.grannyFlat.familyGuide.brief.generatedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} from the family&apos;s answers, which are kept with this file.</p>
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-3 text-xs font-semibold">
                                                <button type="button" onClick={() => setActivePage('GrannyFlatPage')} className="text-slate-600 hover:text-blue-700 hover:underline">Open the guide</button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const guide = factFind.grannyFlat?.familyGuide || {};
                                                        const b = guide.brief || {};
                                                        openGfSolicitorBrief({
                                                            clientName: factFind.client?.firstName || 'you',
                                                            partnerName: factFind.adviceFor === 'couple' ? (factFind.partner?.firstName || null) : null,
                                                            answers: guide,
                                                            figures: b.entryContribution != null ? { entryContribution: Number(b.entryContribution) || 0, interestValue: Number(b.interestValue) || 0, deprivation: Number(b.deprivation) || 0, isHomeowner: !!b.isHomeowner, dateEntered: b.dateEntered || '' } : null,
                                                            generatedAt: b.generatedAt || new Date().toISOString(),
                                                        });
                                                    }}
                                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-700 hover:border-blue-300 hover:text-blue-800"
                                                >
                                                    Print it again
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
                <div className="space-y-8">
                    <NextStepsCard setActivePage={setActivePage} goals={goals} />
                    <WorkflowApplicationsFormsCard factFind={factFind} setActivePage={setActivePage} savedStrategies={savedStrategies} receivesPension={socialSecurity > 0} currentPension={socialSecurity} />
                    <Card className="bg-white/70">
                        <SectionHeader title="Helpful Services" showArrow={false} />
                        <div className="mt-6 p-4 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
                            <MyAgedCareOfficialLogo />
                        </div>
                    </Card>
                </div>
            </div>
            )}

            {hasNothingSaved && showAllSections && (
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={() => setShowAllSections(false)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline"
                    >
                        <ChevronUp size={15} /> Show less
                    </button>
                </div>
            )}
        </div>
    );
}
